import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  driverPinDotRecipe,
  driverPinTagRecipe,
  mapDotPinDotRecipe,
  mapDotPinLabelRecipe,
  mapDotPinRecipe,
  mapLabelPinRecipe,
  mapPinRecipe,
  type DriverPinDotRecipeProps,
  type MapLabelPinRecipeProps,
} from '../recipes/map.recipe';

export type DriverPinTone = NonNullable<DriverPinDotRecipeProps['tone']>;

export interface DriverPinProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  /** Two letters, drawn inside the dot. */
  initials: string;
  tone: DriverPinTone;
  /** What a screen reader calls the pin: "Marcus Webb, en route, 6 min". */
  label: string;
  /** The words beside the dot: an ETA, "On scene", how long since a fix. */
  tag?: string;
  /** The pin whose call or driver is open in the panel. */
  selected?: boolean;
  /** Left out for a pin that only shows a place, such as the replay's truck. */
  onClick?: React.MouseEventHandler<HTMLElement>;
}

/**
 * A driver on the map: their initials in the colour of what they are doing,
 * with a few words beside it.
 *
 *   <DriverPin initials="MW" tone="phase-en-route" tag="6 min"
 *     label="Marcus Webb, en route, 6 min" onClick={open} />
 *
 * The tag is there so the colour never carries the status on its own: an
 * en-route pin says its ETA, a parked one says nothing, a silent one says how
 * long ago it was last heard from.
 */
export const DriverPin = React.forwardRef<HTMLElement, DriverPinProps>(
  function DriverPin(
    {
      initials,
      tone,
      label,
      tag,
      selected = false,
      onClick,
      className,
      ...props
    },
    ref
  ) {
    const body = (
      <>
        <span className={driverPinDotRecipe({ tone, selected })}>
          {initials}
        </span>
        {tag ? <span className={driverPinTagRecipe()}>{tag}</span> : null}
      </>
    );
    if (!onClick) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          role="img"
          aria-label={label}
          className={cn(mapPinRecipe({ interactive: false }), className)}
          {...(props as React.HTMLAttributes<HTMLSpanElement>)}
        >
          {body}
        </span>
      );
    }
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        aria-label={label}
        aria-pressed={selected}
        onClick={onClick}
        className={cn(mapPinRecipe({ interactive: true }), className)}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {body}
      </button>
    );
  }
);

export interface MapLabelPinProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'>,
    MapLabelPinRecipeProps {
  /** The words on the pin: "Pickup", "Drop 11:16 AM", "#1045 waiting 3 min". */
  children: React.ReactNode;
  /** Left out for a pin that is only a label. */
  onClick?: React.MouseEventHandler<HTMLElement>;
}

/**
 * A labelled point on the map: a pickup, a drop, a call nobody is on yet, or
 * a finished call on the history map.
 *
 *   <MapLabelPin tone="waiting" onClick={open}>#1045 waiting 3 min</MapLabelPin>
 */
export const MapLabelPin = React.forwardRef<HTMLElement, MapLabelPinProps>(
  function MapLabelPin({ tone, onClick, className, children, ...props }, ref) {
    if (!onClick) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          className={cn(mapLabelPinRecipe({ tone }), className)}
          {...(props as React.HTMLAttributes<HTMLSpanElement>)}
        >
          {children}
        </span>
      );
    }
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        className={cn(mapPinRecipe({ interactive: true }), className)}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        <span className={mapLabelPinRecipe({ tone })}>{children}</span>
      </button>
    );
  }
);

export interface MapDotPinProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** The words shown on hover, and what a screen reader says: "#95103 · Oct 14". */
  label: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * A call on the map as a small grey dot, its label shown only on hover or
 * keyboard focus. For maps with many calls on them, where a label on every
 * one would cover the map.
 *
 *   <MapDotPin label="#95103 · Oct 14" onClick={() => select(id)} />
 */
export const MapDotPin = React.forwardRef<HTMLButtonElement, MapDotPinProps>(
  function MapDotPin({ label, onClick, className, ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        onClick={onClick}
        className={cn(mapDotPinRecipe(), className)}
        {...props}
      >
        <span className={mapDotPinDotRecipe()} aria-hidden="true" />
        <span className={mapDotPinLabelRecipe()} aria-hidden="true">
          {label}
        </span>
      </button>
    );
  }
);
